import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Users, Video } from "lucide-react";

import pokerVlogsImg from "@/assets/landing/poker-vlogs.png";
import playersImg from "@/assets/landing/players.png";

const slideFromLeft = {
  hidden: { opacity: 0, x: -50 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.7, ease: "easeOut" as const } },
};

const slideFromRight = {
  hidden: { opacity: 0, x: 50 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.7, ease: "easeOut" as const, delay: 0.15 } },
};

const stagger = {
  visible: { transition: { staggerChildren: 0.2 } },
};

export default function CommunitySection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });

  return (
    <motion.section
      ref={ref}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      variants={stagger}
      className="py-20 px-4 bg-secondary/30"
    >
      <div className="max-w-6xl mx-auto">
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="font-display text-2xl sm:text-3xl font-bold uppercase text-center tracking-wide mb-4"
        >
          More Than Tools — A <span className="text-gradient-gold">Community</span>
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="text-muted-foreground text-center max-w-2xl mx-auto mb-12"
        >
          Watch top poker vlogs, find players near you, and connect with a community that's as serious about the game as you are.
        </motion.p>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Poker Vlogs */}
          <motion.div variants={slideFromLeft} className="space-y-4" style={{ perspective: 800 }}>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Video className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h3 className="font-display text-lg font-semibold uppercase tracking-wider text-foreground">
                  Poker Vlogs
                </h3>
                <p className="text-xs text-muted-foreground">Curated library of top poker content</p>
              </div>
            </div>
            <motion.div
              whileHover={{ scale: 1.02, rotateY: 3 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
              className="rounded-lg overflow-hidden border border-border/50 shadow-xl"
            >
              <img src={pokerVlogsImg} alt="Poker Vlogs library" className="w-full h-auto" loading="lazy" />
            </motion.div>
          </motion.div>

          {/* Player Directory */}
          <motion.div variants={slideFromRight} className="space-y-4" style={{ perspective: 800 }}>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Users className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h3 className="font-display text-lg font-semibold uppercase tracking-wider text-foreground">
                  Player Directory
                </h3>
                <p className="text-xs text-muted-foreground">Find and connect with players near you</p>
              </div>
            </div>
            <motion.div
              whileHover={{ scale: 1.02, rotateY: -3 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
              className="rounded-lg overflow-hidden border border-border/50 shadow-xl"
            >
              <img src={playersImg} alt="Player directory" className="w-full h-auto" loading="lazy" />
            </motion.div>
          </motion.div>
        </div>
      </div>
    </motion.section>
  );
}
